import * as Yup from 'yup';

import { validatePhone } from '../../../../helpers/formFieldValidations';
import { US_STATES } from '../../../../constants/formOptions';

const fields = [
  {
    label: 'Type',
    placeholder: 'Select type',
    name: 'type',
    type: 'select',
    options: [
      { value: 'Individual', label: 'Individual' },
      { value: 'Entity', label: 'Entity' },
    ],
    validation: Yup.string(),
  },
  {
    label: 'First Name',
    placeholder: 'Type first name',
    name: 'firstName',
    type: 'text',
    validation: Yup.string().required('This field is required'),
  },
  {
    label: 'Last Name',
    placeholder: 'Type last name',
    name: 'lastName',
    type: 'text',
    validation: Yup.string().required('This field is required'),
  },
  {
    label: 'Email',
    placeholder: 'Type email',
    name: 'email',
    type: 'text',
    validation: Yup.string().email('Invalid email'),
  },
  {
    label: 'Phone',
    placeholder: 'Type phone',
    name: 'phone',
    type: 'phone',
    validation: validatePhone,
  },
  {
    label: 'Date of Birth',
    placeholder: 'Select date of birth',
    name: 'dateOfBirth',
    type: 'date',
    validation: Yup.date(),
  },
  {
    label: 'TIN',
    placeholder: 'Type TIN',
    name: 'tin',
    type: (values) => {
      if (values.type === 'Entity') {
        return 'ein';
      } else if (values.type === 'Individual') {
        return 'ssn';
      } else {
        return 'text';
      }
    },
    validation: Yup.string().test(
      'idValueFormat',
      'Invalid format for selected ID type',
      function (value) {
        const { type } = this.parent;

        if (!value) {
          return true;
        }

        if (type === 'Individual') {
          return /^\d{3}-?\d{2}-?\d{4}$/.test(value);
        } else if (type === 'Entity') {
          return /^\d{2}-\d{7}$/.test(value);
        }

        return true;
      }
    ),
  },
  {
    label: 'TIN Verified',
    name: 'isTinVerified',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Address Line 1',
    placeholder: 'Type address line 1',
    name: 'addressLine1',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'Address Line 2',
    placeholder: 'Type address line 2',
    name: 'addressLine2',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'City',
    placeholder: 'Type city',
    name: 'city',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'State',
    placeholder: 'Select state',
    name: 'state',
    type: 'select',
    options: US_STATES,
    validation: Yup.string(),
  },
  {
    label: 'ZIP',
    placeholder: 'Type ZIP',
    name: 'zip',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Time At Address',
    placeholder: 'Type time at address',
    name: 'timeAtAddress',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Own or Rent',
    placeholder: 'Select own or rent',
    name: 'ownOrRent',
    type: 'select',
    options: [
      { value: 'Rent', label: 'Rent' },
      { value: 'Own', label: 'Own' },
    ],
    validtation: Yup.string(),
  },
  {
    label: 'Rent Amount',
    placeholder: 'Type rent amount',
    name: 'rentAmount',
    type: 'currency',
    validation: Yup.string(),
  },
  {
    label: 'Landlord Name',
    placeholder: 'Type landlord name',
    name: 'landlordName',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Landlord Phone',
    placeholder: 'Type landlord phone',
    name: 'landlordPhone',
    type: 'phone',
    validation: validatePhone,
  },
  {
    label: 'Landlord Email',
    placeholder: 'Landlord email',
    name: 'landlordEmail',
    type: 'text',
    validation: Yup.string().email('Invalid email'),
  },
  {
    label: 'VOR',
    name: 'hasVor',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Dependents',
    placeholder: 'Type dependents',
    name: 'dependents',
    type: 'text',
    validation: Yup.string()
      .typeError('This field must be a number')
      .test(
        'is-comma-separated-integers',
        'This field must be a comma separated list of integers',
        (value) => {
          if (!value) {
            return true;
          }

          const parts = value.split(',').map((item) => item.trim());

          return parts.every((part) => /^-?\d+$/.test(part));
        }
      ),
  },
  {
    label: 'Citizenship',
    placeholder: 'Select citizenship',
    name: 'citizenship',
    type: 'select',
    options: [
      { value: 'U.S. Citizen', label: 'U.S. Citizen' },
      { value: 'Permanent Resident Alien', label: 'Permanent Resident Alien' },
      {
        value: 'Non-Permanent Resident Alien',
        label: 'Non-Permanent Resident Alien',
      },
    ],
    validation: Yup.string(),
  },
  {
    label: 'Marital Status',
    placeholder: 'Select marital status',
    name: 'maritalStatus',
    type: 'select',
    options: [
      { value: 'Married', label: 'Married' },
      { value: 'Separated', label: 'Separated' },
      {
        value: 'Unmarried',
        label: 'Unmarried',
      },
    ],
    validation: Yup.string(),
  },
  {
    label: 'Language',
    placeholder: 'Select language',
    name: 'language',
    type: 'select',
    options: [
      { value: 'English', label: 'English' },
      { value: 'Spanish', label: 'Spanish' },
    ],
    validation: Yup.string(),
  },
  {
    label: 'Gender',
    placeholder: 'Select gender',
    name: 'gender',
    type: 'select',
    options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
    ],
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'Non-Borrower Spouse Name',
    placeholder: 'Type non-borrower spouse name',
    name: 'nonBorrowerSpouseName',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Spouse Photo ID',
    name: 'hasSpousePhotoId',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Previous Address Line 1',
    placeholder: 'Type previous address line 1',
    name: 'previousAddressLine1',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'Previous Address Line 2',
    placeholder: 'Type previous address line 2',
    name: 'previousAddressLine2',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'Previous City',
    placeholder: 'Type previous city',
    name: 'previousCity',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Previous State',
    placeholder: 'Select previous state',
    name: 'previousState',
    type: 'select',
    options: US_STATES,
    validation: Yup.string(),
  },
  {
    label: 'Previous ZIP',
    placeholder: 'Type previous ZIP',
    name: 'previousZip',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Previous Time At Address',
    placeholder: 'Type previous time at address',
    name: 'previousTimeAtAddress',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Previous Own or Rent',
    placeholder: 'Select previous own or rent',
    name: 'previousOwnOrRent',
    type: 'select',
    options: [
      { value: 'Rent', label: 'Rent' },
      { value: 'Own', label: 'Own' },
    ],
    validtation: Yup.string(),
  },
  {
    label: 'Previous Rent Amount',
    placeholder: 'Type previous rent amount',
    name: 'previousRentAmount',
    type: 'currency',
    validation: Yup.string(),
  },
];

export default fields;
